import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook that triggers a component update based on a specified time.
 *
 * This hook sets up listeners for mock date events and schedules a future
 * update when the specified time is reached or when a minimum time threshold
 * is exceeded.
 *
 * @param {string|moment} when - The time at which the update should be triggered.
 * @param {number} [min=0] - The minimum time in milliseconds to wait before
 * triggering an update, if the calculated difference is less.
 */

export function useScheduledUpdate(when, min = 0) {
    const [lastForcedUpdate, setLastForcedUpdate] = useState(),
        forceUpdate = useCallback(() => setLastForcedUpdate(Date.now()), []),
        timediff = -1 * moment().diff(when ?? 0);

    useEffect(() => {
        document.addEventListener('mockdate-start', forceUpdate);
        document.addEventListener('mockdate-tick', forceUpdate);
        document.addEventListener('focus', forceUpdate);
        document.addEventListener('focusin', forceUpdate);
        document.addEventListener('focusout', forceUpdate);
        window.addEventListener('focus', forceUpdate);

        return () => {
            document.removeEventListener('mockdate-start', forceUpdate);
            document.removeEventListener('mockdate-tick', forceUpdate);
            document.removeEventListener('focus', forceUpdate);
            document.removeEventListener('focusin', forceUpdate);
            document.removeEventListener('focusout', forceUpdate);
            window.removeEventListener('focus', forceUpdate);
        };
    }, []);

    useEffect(() => {
        if (when) {
            let diff = timediff;
            if (min && diff < min) diff = min;
            if (diff >= 0) {
                const timeout = setTimeout(forceUpdate, diff);
                return () => clearTimeout(timeout);
            }
        }
    }, [when, lastForcedUpdate]); // change only "when" changes; timediff changes everytime this is executed :)
}
